import React from "react";
import { Helmet } from "react-helmet";

export default function Feature() {
  return (
    <React.Fragment>
      <Helmet>
        <title>Reflex Advisor Reviews | Reflex Advisor</title>
        <meta name="description" content="Reflex Advisor reviews on Arius Business." />
        <link rel="canonical" href="https://ariusbusiness.com/company/reflex-advisor" />
        <meta property="og:url" content="https://ariusbusiness.com/company/reflex-advisor" />
        
        <style>{`
          :root {
            --purple: #7b19bc;
            --purple-light: #d18cf3;
            --purple-hover: #9b24e8;
            --font-display: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            --font-body: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          }
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          html, body { background: #08080a; overflow-x: hidden; }

          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(24px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes lineGrow {
            from { transform: scaleX(0); }
            to   { transform: scaleX(1); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.5; }
            50%       { opacity: 1; }
          }

          .arius-section {
            min-height: 100vh;
            background: #08080a;
            display: flex;
            align-items: center;
            padding: 120px 0;
            position: relative;
            overflow: hidden;
          }

          /* faint radial glow top-left */
          .arius-section::before {
            content: '';
            position: absolute;
            top: -160px;
            left: -160px;
            width: 600px;
            height: 600px;
            background: radial-gradient(circle, rgba(123,25,188,0.12) 0%, transparent 70%);
            pointer-events: none;
          }

          .arius-container {
            max-width: 1100px;
            margin: 0 auto;
            padding: 0 60px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 100px;
            align-items: center;
          }

          /* ── LEFT COLUMN ── */
          .arius-left { display: flex; flex-direction: column; gap: 0; }

          .arius-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 7px 14px 7px 10px;
            border: 1px solid rgba(209,140,243,0.25);
            border-radius: 4px;
            width: fit-content;
            margin-bottom: 36px;
            animation: fadeUp 0.6s ease both;
          }
          .arius-badge-dot {
            width: 6px; height: 6px;
            border-radius: 50%;
            background: #b46ef5;
            animation: pulse 2s ease-in-out infinite;
          }
          .arius-badge span {
            font-family: var(--font-body);
            font-size: 11px;
            font-weight: 500;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #b46ef5;
          }

          .arius-heading {
            font-family: var(--font-display);
            font-size: clamp(36px, 4.5vw, 58px);
            font-weight: 800;
            line-height: 1.02;
            letter-spacing: -0.03em;
            color: #f5f5f7;
            margin-bottom: 28px;
            animation: fadeUp 0.6s 0.1s ease both;
          }
          .arius-heading em {
            font-style: normal;
            color: #b46ef5;
          }

          /* animated underline on heading */
          .arius-heading-rule {
            display: block;
            height: 2px;
            width: 64px;
            background: linear-gradient(90deg, #7b19bc, #d18cf3);
            border-radius: 2px;
            transform-origin: left;
            animation: lineGrow 0.7s 0.4s cubic-bezier(0.16,1,0.3,1) both;
            margin-bottom: 32px;
          }

          .arius-body {
            font-family: var(--font-body);
            font-size: 16px;
            font-weight: 300;
            color: rgba(245,245,247,0.52);
            line-height: 1.8;
            margin-bottom: 16px;
            animation: fadeUp 0.6s 0.2s ease both;
          }
          .arius-body:last-of-type { margin-bottom: 44px; }

          .arius-cta {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 15px 30px;
            background: transparent;
            border: 1px solid rgba(123,25,188,0.55);
            color: #d18cf3;
            font-family: var(--font-body);
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0.04em;
            border-radius: 6px;
            text-decoration: none;
            transition: background 0.25s, border-color 0.25s, color 0.25s, transform 0.2s;
            width: fit-content;
            animation: fadeUp 0.6s 0.3s ease both;
          }
          .arius-cta:hover {
            background: rgba(123,25,188,0.18);
            border-color: #b46ef5;
            color: #f0d6ff;
            transform: translateX(4px);
          }
          .arius-cta-arrow {
            font-size: 16px;
            transition: transform 0.2s;
          }
          .arius-cta:hover .arius-cta-arrow { transform: translateX(3px); }

          /* ── RIGHT COLUMN — CARD ── */
          .arius-card {
            background: #ffffff;
            border: 1px solid rgba(0,0,0,0.06);
            border-radius: 16px;
            padding: 44px 40px;
            position: relative;
            overflow: hidden;
            animation: fadeUp 0.6s 0.25s ease both;
            box-shadow: 0 24px 64px rgba(0,0,0,0.18);
          }
          /* card top accent line */
          .arius-card::before {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0;
            height: 3px;
            background: linear-gradient(90deg, #7b19bc, #d18cf3, #7b19bc);
          }

          .arius-card-logo {
            font-family: var(--font-display);
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: rgba(0,0,0,0.3);
            margin-bottom: 32px;
          }

          .arius-stars {
            display: flex;
            gap: 6px;
            margin-bottom: 20px;
          }
          .arius-star {
            width: 22px; height: 22px;
            background: #ffba00;
            clip-path: polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%);
          }

          .arius-rating-row {
            display: flex;
            align-items: baseline;
            gap: 8px;
            margin-bottom: 28px;
          }
          .arius-rating-num {
            font-family: var(--font-display);
            font-size: 48px;
            font-weight: 800;
            color: #0a0a0a;
            letter-spacing: -0.04em;
            line-height: 1;
          }
          .arius-rating-max {
            font-family: var(--font-body);
            font-size: 16px;
            color: rgba(0,0,0,0.3);
            font-weight: 300;
          }

          .arius-divider {
            height: 1px;
            background: rgba(0,0,0,0.08);
            margin-bottom: 28px;
          }

          .arius-card-label {
            font-family: var(--font-body);
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: rgba(0,0,0,0.35);
            margin-bottom: 10px;
          }
          .arius-card-tagline {
            font-family: var(--font-body);
            font-size: 15px;
            font-weight: 400;
            color: rgba(0,0,0,0.55);
            line-height: 1.65;
            font-style: italic;
            margin-bottom: 32px;
          }

          .arius-card-link {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 20px;
            background: rgba(123,25,188,0.06);
            border: 1px solid rgba(123,25,188,0.2);
            border-radius: 8px;
            text-decoration: none;
            transition: background 0.25s, border-color 0.25s;
          }
          .arius-card-link:hover {
            background: rgba(123,25,188,0.12);
            border-color: rgba(123,25,188,0.4);
          }
          .arius-card-link-text {
            font-family: var(--font-body);
            font-size: 13px;
            font-weight: 500;
            color: #b46ef5;
            letter-spacing: 0.02em;
          }
          .arius-card-link-icon {
            font-size: 16px;
            color: #b46ef5;
          }

          /* ── RESPONSIVE ── */
          @media (max-width: 900px) {
            .arius-container {
              grid-template-columns: 1fr;
              gap: 56px;
              padding: 0 28px;
            }
            .arius-section { padding: 80px 0; }
          }
          @media (max-width: 480px) {
            .arius-heading { font-size: 32px; }
            .arius-card { padding: 32px 24px; }
          }

           
       /* ── Podstatus ── */
.pod-featured-strip {
  background: #F3F4F6;
  padding: 100px 24px;
  text-align: center;
  border-top: 1px solid rgba(255,186,0,0.2);
  border-bottom: 1px solid rgba(255,186,0,0.2);
}

.pod-featured-inner {
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.pod-featured-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #ffba00;
  margin: 0;
}

.pod-featured-h2 {
  font-family: var(--font-display);
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: #000;
  margin: 0;
}

.pod-featured-h2 span {
  color: #ffba00;
}

/* ✅ FIXED TEXT (was invisible) */
.pod-featured-sub {
  font-family: var(--font-body);
  font-size: 16px;
  color: #374151; /* FIXED: dark gray for readability */
  margin: 0;
  line-height: 1.6;
  max-width: 420px;
}

.pod-featured-card {
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 20px;
  padding: 24px 36px;
  background: rgba(255,186,0,0.06);
  border: 1px solid rgba(255,186,0,0.35);
  border-radius: 20px;
  text-decoration: none;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
}

.pod-featured-card:hover {
  background: rgba(255,186,0,0.12);
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(255,186,0,0.15);
}

.pod-featured-card-icon {
  font-size: 36px;
  line-height: 1;
  filter: grayscale(0);
}

.pod-featured-card-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.pod-featured-card-name {
  font-family: var(--font-display);
  font-size: 26px;
  font-weight: 800;
  color: #ffba00;
  letter-spacing: -0.03em;
  line-height: 1;
}
.pod-featured-card-desc {
  font-size: 13px;
  color: #4B5563; /* FIXED: readable gray */
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

        `}</style>
      </Helmet>

      <section className="arius-section">
        <div className="arius-container">

          {/* LEFT */}
          <div className="arius-left">
            <div className="arius-badge">
              <span className="arius-badge-dot" />
              <span>As Featured On</span>
            </div>

            <h1 className="arius-heading">
              Reflex Advisor<br />
              Featured on <em>Arius Business</em>
            </h1>

            <span className="arius-heading-rule" />

            <p className="arius-body">
              Reflex Advisor has been featured on Arius Business, a platform known for
              reviewing and highlighting businesses across different industries.
            </p>
            <p className="arius-body">
              Their review mentions Reflex Advisor and shares insights about our services,
              reputation, and customer experience.
            </p>

            <a
              href="https://ariusbusiness.com/company/reflex-advisor"
              target="_blank"
              rel="noopener noreferrer"
              className="arius-cta"
            >
              See Complete Review &amp; Full Rating
              <span className="arius-cta-arrow">→</span>
            </a>
          </div>

          {/* RIGHT — CARD */}
          <div className="arius-card">
            <p className="arius-card-logo">Arius Business</p>

            <div className="arius-stars">
              {[1,2,3,4,5].map(i => <div className="arius-star" key={i} />)}
            </div>

            <div className="arius-rating-row">
              <span className="arius-rating-num">5.0</span>
              <span className="arius-rating-max">/ 5.0</span>
            </div>

            <div className="arius-divider" />

            <p className="arius-card-label">Review Highlights</p>
            <p className="arius-card-tagline">
              "Recognized for exceptional service quality, strong reputation, and
              outstanding customer experience in the growth marketing space."
            </p>

            <a
              href="https://ariusbusiness.com/company/reflex-advisor"
              target="_blank"
              rel="noopener noreferrer"
              className="arius-card-link"
            >
              <span className="arius-card-link-text">ariusbusiness.com / reflex-advisor</span>
              <span className="arius-card-link-icon">↗</span>
            </a>
          </div>

        </div>
      </section>

      {/* ─── PODSTATUS SECTION ─── */}
      <section className="pod-featured-strip">
  <div className="pod-featured-inner">
    <p className="pod-featured-eyebrow">As Seen On</p>

    <h2 className="pod-featured-h2">
      Officially <span>Listed</span> &amp; Featured
    </h2>

    <p className="pod-featured-sub">
      Recognized and indexed across top podcast discovery platforms.
    </p>

    <a
      href="https://podstatus.com/podcasts/reflex-advisor-junk-removal-growth-system-1381602"
      target="_blank"
      rel="noopener noreferrer"
      className="pod-featured-card"
    >
      <div className="pod-featured-card-icon">📡</div>

      <div className="pod-featured-card-body">
        <span className="pod-featured-card-name">PodStatus.com</span>
        <span className="pod-featured-card-desc">View our listing ↗</span>
      </div>
    </a>
  </div>
      </section>

    </React.Fragment>
  );
}